import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { MedsiScanVitalSigns } from 'lib/medsi/scan-compat';

interface ScanLiveVitalsProps {
  vitalSigns: MedsiScanVitalSigns | null;
  compact?: boolean;
}

function VitalStat({
  label,
  value,
  unit,
  compact = false,
}: {
  label: string;
  value: string;
  unit: string;
  compact?: boolean;
}) {
  return (
    <div
      className={
        compact
          ? 'flex flex-col items-center justify-center flex-1 min-w-0 gap-0 px-1 py-1.5 mx-0.5 bg-white shadow-sm rounded-lg'
          : 'flex flex-col items-center justify-center flex-1 min-w-0 gap-0.5 px-1.5 py-2 mx-0.5 bg-white shadow-sm rounded-xl sm:px-3 sm:py-3 md:py-4'
      }
    >
      <p
        className={
          compact
            ? 'text-[9px] text-gray-500 text-center truncate w-full leading-tight'
            : 'text-[10px] text-gray-500 text-center truncate w-full leading-tight sm:text-xs'
        }
      >
        {label}
      </p>
      <p
        className={
          compact
            ? 'text-base font-bold text-black leading-none'
            : 'text-lg font-bold text-black leading-none sm:text-xl md:text-2xl'
        }
      >
        {value}
      </p>
      <p
        className={
          compact
            ? 'text-[10px] text-gray-600 leading-none'
            : 'text-xs text-gray-600 leading-none sm:text-sm'
        }
      >
        {unit}
      </p>
    </div>
  );
}

function formatVital(
  enabled: boolean | undefined,
  value: number | null | undefined,
): string {
  if (!enabled || value == null) return '--';
  return String(value);
}

function ScanLiveVitalsImpl({
  vitalSigns,
  compact = false,
}: ScanLiveVitalsProps) {
  const { t } = useTranslation();

  const hr = formatVital(
    vitalSigns?.pulseRate.isEnabled,
    vitalSigns?.pulseRate.value,
  );
  const rr = formatVital(
    vitalSigns?.respirationRate.isEnabled,
    vitalSigns?.respirationRate.value,
  );
  const spo2 = formatVital(vitalSigns?.spo2?.isEnabled, vitalSigns?.spo2?.value);

  return (
    <div className={compact ? 'flex w-full gap-0.5' : 'flex w-full gap-1 sm:gap-2'}>
      <VitalStat
        compact={compact}
        label={t('heart_rate')}
        value={hr}
        unit={t('unit_bpm')}
      />
      <VitalStat
        compact={compact}
        label={t('breathing_rate')}
        value={rr}
        unit={t('unit_rpm')}
      />
      <VitalStat compact={compact} label={t('spo2')} value={spo2} unit='%' />
    </div>
  );
}

export const ScanLiveVitals = memo(ScanLiveVitalsImpl);
